import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search, X, ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
  Package, Loader2, RefreshCw, Building2, ExternalLink, Copy,
  ShoppingCart, AlertTriangle, XCircle, Hash, Filter, Shield,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/Admin/Layout/AdminLayout';
import apiClient from '../../services/api/client';
import { ENDPOINTS } from '../../services/api/endpoints';
import { useSeller } from '../../context/SellerContext';
import MarketplaceSelector from '../../components/shared/MarketplaceSelector';

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZES = [25, 50, 100];

const platformConfig = {
  AMAZON: { letter: 'A', gradient: 'from-orange-500 to-amber-500', text: 'text-white', label: 'Amazon' },
  FLIPKART: { letter: 'F', gradient: 'from-blue-600 to-blue-500', text: 'text-white', label: 'Flipkart' },
  MEESHO: { letter: 'M', gradient: 'from-purple-600 to-violet-500', text: 'text-white', label: 'Meesho' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatINR = (n) => {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
};

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const now = new Date();
  const date = new Date(dateStr);
  const diffMins = Math.floor((now - date) / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
}

function copyToClipboard(text) {
  navigator.clipboard.writeText(text);
  toast.success('Copied to clipboard');
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color = 'gray', icon: Icon, active, onClick }) {
  const colorMap = {
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    blue: 'bg-[#FFF4E6] text-[#C45500] border-[#FFD89E]',
    indigo: 'bg-[#FFF4E6] text-[#C45500] border-[#FFD89E]',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    gray: 'bg-slate-50 text-slate-700 border-slate-200',
  };
  return (
    <div
      onClick={onClick}
      className={`rounded-xl border p-4 transition-all ${onClick ? 'cursor-pointer hover:shadow-md' : ''} ${
        active ? 'ring-2 ring-[#f08804] shadow-sm' : ''
      } ${colorMap[color] || colorMap.gray}`}
    >
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold uppercase tracking-widest opacity-70">{label}</p>
        {Icon && <Icon size={15} className="opacity-40" />}
      </div>
      <p className="text-2xl font-bold mt-1.5">{value}</p>
      {sub && <p className="text-[11px] mt-1 opacity-70">{sub}</p>}
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    ACTIVE: { label: 'Active', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    INACTIVE: { label: 'Inactive', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
    INCOMPLETE: { label: 'Incomplete', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    SUPPRESSED: { label: 'Suppressed', cls: 'bg-red-100 text-red-700 border-red-200' },
  };
  const c = config[status] || { label: status || '—', cls: 'bg-slate-100 text-slate-500 border-slate-200' };
  return (
    <span className={`inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full border ${c.cls}`}>
      {c.label}
    </span>
  );
}

function BuyBoxBadge({ isMine }) {
  if (isMine === true) return <span className="inline-flex items-center text-[10px] font-bold text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full">Won</span>;
  if (isMine === false) return <span className="inline-flex items-center text-[10px] font-bold text-red-700 bg-red-100 border border-red-200 px-2 py-0.5 rounded-full">Lost</span>;
  return <span className="inline-flex items-center text-[10px] font-bold text-slate-400 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full">N/A</span>;
}

function FulfillmentBadge({ channel }) {
  const isFBA = channel && channel !== 'DEFAULT';
  if (isFBA) return <span className="inline-flex items-center text-[10px] font-bold text-orange-700 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-md">FBA</span>;
  return <span className="inline-flex items-center text-[10px] font-bold text-[#C45500] bg-[#FFF4E6] border border-[#FFD89E] px-2 py-0.5 rounded-md">FBM</span>;
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ProductsPage() {
  const { selectedSeller: sellerCtx } = useSeller();
  const sellerId = sellerCtx?.id || '';
  const sellerChannels = useMemo(() => (sellerCtx?.channels || []).filter(c => c.accountStatus === 'AUTHORIZED'), [sellerCtx]);
  const [selectedAccountId, setSelectedAccountId] = useState('');

  // Data state
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [checkingBuyBox, setCheckingBuyBox] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fulfillmentFilter, setFulfillmentFilter] = useState('');
  const [buyBoxFilter, setBuyBoxFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [asinFilter, setAsinFilter] = useState('');

  // Sort state
  const [sortField, setSortField] = useState('title');
  const [sortDir, setSortDir] = useState('asc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Expand
  const [expandedId, setExpandedId] = useState(null);

  // Clear products if seller changes and has no channels
  useEffect(() => {
    if (sellerChannels.length === 0) {
      setSelectedAccountId('');
      setProducts([]);
    }
  }, [sellerChannels]);

  // Load products — always pass sellerId, add sellerAccountId when specific marketplace selected
  const loadProducts = useCallback(async () => {
    if (!sellerId) return;
    setLoadingProducts(true);
    try {
      const params = new URLSearchParams();
      params.append('sellerId', sellerId);
      if (selectedAccountId) params.append('sellerAccountId', selectedAccountId);
      params.append('size', '1000');
      params.append('sortBy', 'title');
      params.append('sortDir', 'asc');
      const res = await apiClient.get(`${ENDPOINTS.PRODUCTS.LIST}?${params.toString()}`);
      const data = res.data || res;
      const items = data.content || data || [];
      setProducts(Array.isArray(items) ? items : []);
    } catch (err) {
      if (err?.response?.status !== 404) {
        toast.error('Failed to load products');
      }
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, [selectedAccountId, sellerId]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  // Sync products
  const handleSync = async () => {
    if (!selectedAccountId) {
      toast.error('Please select a seller account to sync');
      return;
    }
    setSyncing(true);
    const toastId = toast.loading('Syncing product catalog... This may take a minute.');
    try {
      const res = await apiClient.post(`${ENDPOINTS.PRODUCTS.SYNC}?sellerAccountId=${selectedAccountId}`);
      const data = res.data || res;
      toast.dismiss(toastId);
      if (data.syncStatus === 'FAILED') {
        toast.error(data.syncMessage || 'Product sync failed');
      } else {
        toast.success(`Synced ${data.totalProducts || 0} products (${data.newProducts || 0} new, ${data.updatedProducts || 0} updated)`);
        loadProducts();
      }
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err?.response?.data?.message || 'Product sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // Check Buy Box (hijack check)
  const handleBuyBoxCheck = async () => {
    if (!selectedAccountId) {
      toast.error('Please select a seller account first');
      return;
    }
    if (products.length === 0) {
      toast.error('Sync products first before checking Buy Box');
      return;
    }
    setCheckingBuyBox(true);
    const toastId = toast.loading(`Checking Buy Box for ${products.length} product${products.length !== 1 ? 's' : ''}... This may take a minute.`);
    try {
      const res = await apiClient.post(`${ENDPOINTS.REPORTS.HIJACK_CHECK}?sellerAccountId=${selectedAccountId}`);
      const data = res.data || res;
      toast.dismiss(toastId);
      const safe = data.safeCount ?? 0;
      const hijacked = (data.hijackedBuyBoxRetainedCount ?? 0) + (data.hijackedBuyBoxLostCount ?? 0);
      const suppressed = data.buyBoxSuppressedCount ?? 0;
      if (hijacked > 0) {
        toast.error(`Buy Box check complete — ${hijacked} hijacked, ${suppressed} suppressed, ${safe} safe`);
      } else {
        toast.success(`Buy Box check complete — all ${safe} products safe!`);
      }
      loadProducts();
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err?.response?.data?.message || 'Buy Box check failed');
    } finally {
      setCheckingBuyBox(false);
    }
  };

  // Client-side filtering
  const filteredProducts = useMemo(() => {
    let result = [...products];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(p =>
        (p.title && p.title.toLowerCase().includes(q)) ||
        (p.asin && p.asin.toLowerCase().includes(q)) ||
        (p.sku && p.sku.toLowerCase().includes(q)) ||
        (p.brand && p.brand.toLowerCase().includes(q))
      );
    }

    if (statusFilter) {
      result = result.filter(p => p.listingStatus === statusFilter);
    }

    if (fulfillmentFilter) {
      if (fulfillmentFilter === 'FBA') {
        result = result.filter(p => p.fulfillmentChannel && p.fulfillmentChannel !== 'DEFAULT');
      } else {
        result = result.filter(p => !p.fulfillmentChannel || p.fulfillmentChannel === 'DEFAULT');
      }
    }

    if (buyBoxFilter) {
      if (buyBoxFilter === 'WINNING') result = result.filter(p => p.buyBoxIsMine === true);
      else if (buyBoxFilter === 'LOSING') result = result.filter(p => p.buyBoxIsMine === false);
    }

    if (stockFilter) {
      if (stockFilter === 'IN_STOCK') result = result.filter(p => p.quantity != null && p.quantity > 0);
      else if (stockFilter === 'OUT_OF_STOCK') result = result.filter(p => p.quantity == null || p.quantity <= 0);
    }

    if (asinFilter) {
      if (asinFilter === 'HAS_ASIN') result = result.filter(p => p.asin && p.asin.trim() !== '');
      else if (asinFilter === 'NO_ASIN') result = result.filter(p => !p.asin || p.asin.trim() === '');
      else if (asinFilter === 'NO_PRICE') result = result.filter(p => p.price == null || p.price <= 0);
      else if (asinFilter === 'MULTI_SKU') {
        // Find ASINs that appear more than once
        const asinCount = {};
        products.forEach(p => { if (p.asin) asinCount[p.asin] = (asinCount[p.asin] || 0) + 1; });
        const multiAsins = new Set(Object.entries(asinCount).filter(([, c]) => c > 1).map(([a]) => a));
        result = result.filter(p => p.asin && multiAsins.has(p.asin));
      }
    }

    // Sort
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (aVal == null) aVal = sortDir === 'asc' ? Infinity : -Infinity;
      if (bVal == null) bVal = sortDir === 'asc' ? Infinity : -Infinity;
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [products, searchQuery, statusFilter, fulfillmentFilter, buyBoxFilter, stockFilter, asinFilter, sortField, sortDir]);

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / pageSize);
  const paginatedProducts = filteredProducts.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  // Reset page on filter change
  useEffect(() => { setCurrentPage(0); }, [searchQuery, statusFilter, fulfillmentFilter, buyBoxFilter, pageSize]);

  // Stats
  const stats = useMemo(() => {
    const total = products.length;
    const active = products.filter(p => p.listingStatus === 'ACTIVE').length;
    const inactive = products.filter(p => p.listingStatus === 'INACTIVE').length;
    const buyBoxWon = products.filter(p => p.buyBoxIsMine === true).length;
    const buyBoxLost = products.filter(p => p.buyBoxIsMine === false).length;
    const outOfStock = products.filter(p => p.quantity != null && p.quantity === 0).length;
    const fba = products.filter(p => p.fulfillmentChannel && p.fulfillmentChannel !== 'DEFAULT').length;
    return { total, active, inactive, buyBoxWon, buyBoxLost, outOfStock, fba };
  }, [products]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setCurrentPage(0);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />;
  };

  const hasActiveFilters = searchQuery || statusFilter || fulfillmentFilter || buyBoxFilter || stockFilter || asinFilter;

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setFulfillmentFilter('');
    setBuyBoxFilter('');
    setStockFilter('');
    setAsinFilter('');
    setCurrentPage(0);
  };

  const lastSyncTime = products.length > 0
    ? products.reduce((latest, p) => {
        if (!p.lastSyncAt) return latest;
        return !latest || new Date(p.lastSyncAt) > new Date(latest) ? p.lastSyncAt : latest;
      }, null)
    : null;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <Loader2 size={32} className="mx-auto text-[#f08804] animate-spin mb-3" />
            <p className="text-sm text-slate-500">Loading...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-5 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Product Listings</h1>
            <p className="text-xs text-slate-400 mt-0.5">
              Synced product listings &amp; competitive intelligence
              {lastSyncTime && <span className="ml-2 text-slate-400">· Last sync: {timeAgo(lastSyncTime)}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2.5">
            {/* Marketplace Picker */}
            <MarketplaceSelector value={selectedAccountId} onChange={(v) => { setSelectedAccountId(v); setCurrentPage(0); }} />
            <button onClick={loadProducts} disabled={loading}
              className="p-2.5 rounded-lg bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors disabled:opacity-50" title="Refresh">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={handleBuyBoxCheck} disabled={checkingBuyBox || syncing || !selectedAccountId || products.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-700 border border-slate-200 rounded-lg text-xs font-semibold hover:bg-slate-50 hover:border-slate-300 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
              <Shield size={14} className={checkingBuyBox ? 'animate-pulse text-[#f08804]' : ''} />
              {checkingBuyBox ? 'Checking...' : 'Check Buy Box'}
            </button>
            <button onClick={handleSync} disabled={syncing || checkingBuyBox || !selectedAccountId}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#232F3E] text-white rounded-lg text-xs font-semibold hover:bg-[#37475A] transition-colors shadow-sm shadow-[#FFD89E] disabled:opacity-50 disabled:cursor-not-allowed">
              <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
              {syncing ? 'Syncing...' : 'Sync Products'}
            </button>
          </div>
        </div>

        {/* Stats */}
        {products.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <StatCard label="Total" value={stats.total} icon={Package} color="indigo"
              active={!statusFilter && !buyBoxFilter && !stockFilter && !fulfillmentFilter}
              onClick={() => { clearFilters(); }} />
            <StatCard label="Active" value={stats.active} icon={Package} color="green"
              active={statusFilter === 'ACTIVE'}
              onClick={() => { clearFilters(); setStatusFilter(statusFilter === 'ACTIVE' ? '' : 'ACTIVE'); }} />
            <StatCard label="Inactive" value={stats.inactive} icon={XCircle} color="gray"
              active={statusFilter === 'INACTIVE'}
              onClick={() => { clearFilters(); setStatusFilter(statusFilter === 'INACTIVE' ? '' : 'INACTIVE'); }} />
            <StatCard label="Buy Box Won" value={stats.buyBoxWon} icon={ShoppingCart} color="green"
              active={buyBoxFilter === 'WINNING'}
              sub={stats.buyBoxLost > 0 ? `${stats.buyBoxLost} losing` : undefined}
              onClick={() => { clearFilters(); setBuyBoxFilter(buyBoxFilter === 'WINNING' ? '' : 'WINNING'); }} />
            <StatCard label="Buy Box Lost" value={stats.buyBoxLost} icon={ShoppingCart} color="red"
              active={buyBoxFilter === 'LOSING'}
              onClick={() => { clearFilters(); setBuyBoxFilter(buyBoxFilter === 'LOSING' ? '' : 'LOSING'); }} />
            <StatCard label="Out of Stock" value={stats.outOfStock} icon={AlertTriangle} color="amber"
              active={stockFilter === 'OUT_OF_STOCK'}
              onClick={() => { clearFilters(); setStockFilter(stockFilter === 'OUT_OF_STOCK' ? '' : 'OUT_OF_STOCK'); }} />
            <StatCard label="FBA" value={stats.fba} icon={Package} color="orange"
              active={fulfillmentFilter === 'FBA'}
              sub={`${stats.total - stats.fba} FBM`}
              onClick={() => { clearFilters(); setFulfillmentFilter(fulfillmentFilter === 'FBA' ? '' : 'FBA'); }} />
          </div>
        )}

        {/* Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          {/* Row 1: Search + primary filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search title, ASIN, SKU, brand..."
                className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]" />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
                  <X size={14} />
                </button>
              )}
            </div>

            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]">
              <option value="">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="INCOMPLETE">Incomplete</option>
              <option value="DRAFT">Draft</option>
              <option value="CLOSED">Closed</option>
            </select>

            <select value={fulfillmentFilter} onChange={(e) => setFulfillmentFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]">
              <option value="">Fulfillment: All</option>
              <option value="FBA">FBA</option>
              <option value="FBM">FBM</option>
            </select>

            <select value={buyBoxFilter} onChange={(e) => setBuyBoxFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]">
              <option value="">Buy Box: All</option>
              <option value="WINNING">Winning</option>
              <option value="LOSING">Losing</option>
            </select>

            <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]">
              <option value="">Stock: All</option>
              <option value="IN_STOCK">In Stock</option>
              <option value="OUT_OF_STOCK">Out of Stock</option>
            </select>

            <select value={asinFilter} onChange={(e) => setAsinFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]">
              <option value="">More Filters</option>
              <option value="HAS_ASIN">Has ASIN</option>
              <option value="NO_ASIN">No ASIN</option>
              <option value="NO_PRICE">No Price</option>
              <option value="MULTI_SKU">Multi-SKU ASINs</option>
            </select>

            {hasActiveFilters && (
              <button onClick={clearFilters} className="inline-flex items-center px-3 py-2 text-sm text-red-600 hover:text-red-800">
                <X size={14} className="mr-1" /> Clear
              </button>
            )}
          </div>

          {/* Row 2: Active filter tags + count */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              {statusFilter && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#FFF4E6] text-[#C45500] text-xs font-medium rounded-full border border-[#FFD89E]">
                  Status: {statusFilter}
                  <button onClick={() => setStatusFilter('')} className="hover:text-[#131921]"><X size={12} /></button>
                </span>
              )}
              {fulfillmentFilter && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#FFF4E6] text-[#C45500] text-xs font-medium rounded-full border border-[#FFD89E]">
                  {fulfillmentFilter}
                  <button onClick={() => setFulfillmentFilter('')} className="hover:text-[#131921]"><X size={12} /></button>
                </span>
              )}
              {buyBoxFilter && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#FFF4E6] text-[#C45500] text-xs font-medium rounded-full border border-[#FFD89E]">
                  Buy Box: {buyBoxFilter === 'WINNING' ? 'Won' : 'Lost'}
                  <button onClick={() => setBuyBoxFilter('')} className="hover:text-[#131921]"><X size={12} /></button>
                </span>
              )}
              {stockFilter && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#FFF4E6] text-[#C45500] text-xs font-medium rounded-full border border-[#FFD89E]">
                  {stockFilter === 'IN_STOCK' ? 'In Stock' : 'Out of Stock'}
                  <button onClick={() => setStockFilter('')} className="hover:text-[#131921]"><X size={12} /></button>
                </span>
              )}
              {asinFilter && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#FFF4E6] text-[#C45500] text-xs font-medium rounded-full border border-[#FFD89E]">
                  {asinFilter === 'HAS_ASIN' ? 'Has ASIN' : asinFilter === 'NO_ASIN' ? 'No ASIN' : asinFilter === 'NO_PRICE' ? 'No Price' : 'Multi-SKU'}
                  <button onClick={() => setAsinFilter('')} className="hover:text-[#131921]"><X size={12} /></button>
                </span>
              )}
              {searchQuery && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#FFF4E6] text-[#C45500] text-xs font-medium rounded-full border border-[#FFD89E]">
                  Search: "{searchQuery}"
                  <button onClick={() => setSearchQuery('')} className="hover:text-[#131921]"><X size={12} /></button>
                </span>
              )}
            </div>
            <span className="text-sm text-gray-500">
              {filteredProducts.length === products.length
                ? `${products.length} products`
                : `${filteredProducts.length} of ${products.length} products`}
            </span>
          </div>
        </div>

        {/* No channels */}
        {sellerChannels.length === 0 && sellerCtx && (
          <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FFE8C8] to-[#FFF4E6] flex items-center justify-center mx-auto mb-5 border border-[#FFE8C8]">
              <Package size={28} className="text-[#f08804]" />
            </div>
            <h3 className="text-lg font-bold text-slate-700 mb-2">No connected channels</h3>
            <p className="text-sm text-slate-400 max-w-sm mx-auto">Connect a marketplace channel on the Sellers page first to sync product listings.</p>
          </div>
        )}

        {/* Loading */}
        {loadingProducts && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm py-16 text-center">
            <Loader2 size={28} className="mx-auto text-[#f08804] animate-spin mb-3" />
            <p className="text-sm text-slate-500">Loading products...</p>
          </div>
        )}

        {/* Empty state */}
        {!loadingProducts && selectedAccountId && products.length === 0 && (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 py-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center mx-auto mb-5 border border-slate-200">
              <Package size={28} className="text-slate-400" />
            </div>
            <h3 className="text-lg font-bold text-slate-700 mb-2">No products synced yet</h3>
            <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">Click "Sync Products" to pull all your listings from this marketplace channel.</p>
            <button onClick={handleSync} disabled={syncing}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#232F3E] text-white rounded-lg text-sm font-semibold hover:bg-[#37475A] transition-colors shadow-sm shadow-[#FFD89E] disabled:opacity-50">
              {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              {syncing ? 'Syncing...' : 'Sync Products Now'}
            </button>
          </div>
        )}

        {/* Product Table */}
        {!loadingProducts && products.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-slate-200 table-fixed">
                <thead className="bg-slate-50/80">
                  <tr>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest cursor-pointer hover:text-slate-700" style={{ width: '42%' }} onClick={() => handleSort('title')}>
                      <span className="flex items-center gap-1">Product <SortIcon field="title" /></span>
                    </th>
                    <th className="px-3 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest cursor-pointer hover:text-slate-700" style={{ width: '9%' }} onClick={() => handleSort('listingStatus')}>
                      <span className="flex items-center gap-1">Status <SortIcon field="listingStatus" /></span>
                    </th>
                    <th className="px-3 py-3 text-center text-[10px] font-bold text-slate-500 uppercase tracking-widest cursor-pointer hover:text-slate-700" style={{ width: '10%' }} onClick={() => handleSort('fulfillmentChannel')}>
                      <span className="flex items-center justify-center gap-1">Fulfill. <SortIcon field="fulfillmentChannel" /></span>
                    </th>
                    <th className="px-3 py-3 text-right text-[10px] font-bold text-slate-500 uppercase tracking-widest cursor-pointer hover:text-slate-700" style={{ width: '10%' }} onClick={() => handleSort('price')}>
                      <span className="flex items-center justify-end gap-1">Price <SortIcon field="price" /></span>
                    </th>
                    <th className="px-3 py-3 text-center text-[10px] font-bold text-slate-500 uppercase tracking-widest" style={{ width: '9%' }}>
                      Buy Box
                    </th>
                    <th className="px-3 py-3 text-center text-[10px] font-bold text-slate-500 uppercase tracking-widest cursor-pointer hover:text-slate-700" style={{ width: '8%' }} onClick={() => handleSort('numberOfOffers')}>
                      <span className="flex items-center justify-center gap-1">Offers <SortIcon field="numberOfOffers" /></span>
                    </th>
                    <th className="px-3 py-3 text-right text-[10px] font-bold text-slate-500 uppercase tracking-widest cursor-pointer hover:text-slate-700" style={{ width: '8%' }} onClick={() => handleSort('quantity')}>
                      <span className="flex items-center justify-end gap-1">Stock <SortIcon field="quantity" /></span>
                    </th>
                    <th className="py-3 text-center text-[10px] font-bold text-slate-500 uppercase tracking-widest" style={{ width: '4%' }}>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {paginatedProducts.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center">
                        <Filter size={28} className="mx-auto text-slate-300 mb-3" />
                        <p className="text-sm font-semibold text-slate-500 mb-1">No products match your filters</p>
                        <p className="text-xs text-slate-400">Try adjusting your search or filter criteria.</p>
                      </td>
                    </tr>
                  ) : (
                    paginatedProducts.map((p) => {
                      const priceDiff = (p.buyBoxPrice != null && p.price != null) ? p.buyBoxPrice - p.price : null;
                      const isExpanded = expandedId === p.id;
                      const isFBA = p.fulfillmentChannel && p.fulfillmentChannel !== 'DEFAULT';

                      return (
                        <React.Fragment key={p.id}>
                          <tr className={`cursor-pointer transition-colors ${isExpanded ? 'bg-[#FFF4E6]/40' : 'hover:bg-slate-50/70'}`}
                            onClick={() => setExpandedId(isExpanded ? null : p.id)}>
                            {/* Product: Image + Title + ASIN */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg border border-slate-200 bg-slate-50 flex-shrink-0 overflow-hidden flex items-center justify-center">
                                  {p.imageUrl ? (
                                    <img src={p.imageUrl} alt="" className="w-full h-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                                  ) : (
                                    <Package size={16} className="text-slate-300" />
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 group">
                                    <p className="text-sm font-semibold text-slate-800 truncate" title={p.title}>{p.title || 'Untitled'}</p>
                                    <button onClick={(e) => { e.stopPropagation(); copyToClipboard(p.title); }}
                                      className="p-0.5 rounded hover:bg-slate-100 text-slate-300 hover:text-slate-500 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0" title="Copy title">
                                      <Copy size={11} />
                                    </button>
                                  </div>
                                  <span className="text-[11px] text-slate-400 font-mono">{p.asin}</span>
                                </div>
                              </div>
                            </td>

                            {/* Status */}
                            <td className="px-4 py-3">
                              <StatusBadge status={p.listingStatus} />
                            </td>

                            {/* Fulfillment */}
                            <td className="px-4 py-3 text-center">
                              <FulfillmentBadge channel={p.fulfillmentChannel} />
                            </td>

                            {/* Price */}
                            <td className="px-4 py-3 text-right text-sm font-semibold text-slate-800">
                              {formatINR(p.price)}
                            </td>

                            {/* Buy Box Status */}
                            <td className="px-4 py-3 text-center">
                              <BuyBoxBadge isMine={p.buyBoxIsMine} />
                            </td>

                            {/* Offers */}
                            <td className="px-4 py-3 text-center">
                              {p.numberOfOffers != null ? (
                                <span className={`text-xs font-bold ${p.numberOfOffers > 1 ? 'text-amber-600' : 'text-slate-500'}`}>
                                  {p.numberOfOffers}
                                </span>
                              ) : (
                                <span className="text-xs text-slate-300">—</span>
                              )}
                            </td>

                            {/* Stock */}
                            <td className="px-4 py-3 text-right">
                              {p.quantity != null ? (
                                <span className={`text-xs font-semibold ${p.quantity > 0 ? 'text-slate-700' : 'text-red-500'}`}>
                                  {p.quantity > 0 ? p.quantity : 'OOS'}
                                </span>
                              ) : (
                                <span className="text-xs text-slate-300">—</span>
                              )}
                            </td>

                            {/* Expand chevron */}
                            <td className="px-3 py-3 text-center">
                              <div className={`inline-flex p-1 rounded-md transition-colors ${isExpanded ? 'bg-[#FFE8C8] text-[#C45500]' : 'text-slate-300'}`}>
                                {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                              </div>
                            </td>
                          </tr>

                          {/* ── Expanded Detail Row ── */}
                          {isExpanded && (
                            <tr className="bg-gradient-to-b from-[#FFF9EE]/30 to-white">
                              <td colSpan={8} className="px-5 pb-5 pt-3">
                                <div className="flex gap-5">
                                  {/* Left: Larger Product Image */}
                                  <div className="w-28 h-28 rounded-xl border border-slate-200 bg-white flex-shrink-0 overflow-hidden flex items-center justify-center shadow-sm">
                                    {p.imageUrl ? (
                                      <img src={p.imageUrl} alt="" className="w-full h-full object-contain p-1" onError={(e) => { e.target.style.display = 'none'; }} />
                                    ) : (
                                      <Package size={32} className="text-slate-200" />
                                    )}
                                  </div>

                                  {/* Right: Detail Content */}
                                  <div className="flex-1 min-w-0 space-y-4">
                                    {/* Pricing Comparison Cards */}
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                      <div className={`rounded-xl px-4 py-3.5 border shadow-sm ${p.buyBoxIsMine === true ? 'bg-emerald-50/60 border-emerald-200' : p.buyBoxIsMine === false ? 'bg-red-50/60 border-red-200' : 'bg-white border-slate-200'}`}>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Buy Box Price</p>
                                        <p className={`text-lg font-bold ${p.buyBoxIsMine === false ? 'text-red-700' : 'text-slate-800'}`}>{formatINR(p.buyBoxPrice)}</p>
                                        {priceDiff != null && priceDiff !== 0 && (
                                          <p className={`text-[10px] font-semibold mt-1 ${priceDiff < 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                                            {priceDiff > 0 ? '▲ +' : '▼ '}{formatINR(Math.abs(priceDiff))} vs your price
                                          </p>
                                        )}
                                        {p.buyBoxIsMine === true && <p className="text-[10px] font-bold text-emerald-600 mt-1">You own the Buy Box</p>}
                                        {p.buyBoxIsMine === false && <p className="text-[10px] font-bold text-red-500 mt-1">Lost to competitor</p>}
                                      </div>
                                      <div className="bg-white rounded-xl px-4 py-3.5 border border-slate-200 shadow-sm">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Lowest FBA</p>
                                        <p className="text-lg font-bold text-slate-800">{formatINR(p.lowestFbaPrice)}</p>
                                        {p.lowestFbaPrice != null && p.price != null && p.lowestFbaPrice < p.price && (
                                          <p className="text-[10px] font-semibold text-red-500 mt-1">
                                            {formatINR(p.price - p.lowestFbaPrice)} below yours
                                          </p>
                                        )}
                                      </div>
                                      <div className="bg-white rounded-xl px-4 py-3.5 border border-slate-200 shadow-sm">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Lowest FBM</p>
                                        <p className="text-lg font-bold text-slate-800">{formatINR(p.lowestFbmPrice)}</p>
                                        {p.lowestFbmPrice != null && p.price != null && p.lowestFbmPrice < p.price && (
                                          <p className="text-[10px] font-semibold text-red-500 mt-1">
                                            {formatINR(p.price - p.lowestFbmPrice)} below yours
                                          </p>
                                        )}
                                      </div>
                                      <div className="bg-white rounded-xl px-4 py-3.5 border border-slate-200 shadow-sm">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Competitors</p>
                                        <p className={`text-lg font-bold ${p.numberOfOffers > 1 ? 'text-amber-600' : 'text-slate-800'}`}>
                                          {p.numberOfOffers != null ? p.numberOfOffers : '—'}
                                        </p>
                                        <p className="text-[10px] text-slate-400 mt-1">
                                          {p.numberOfOffers != null ? `${p.numberOfOffers} seller${p.numberOfOffers !== 1 ? 's' : ''} on this listing` : 'No hijack data yet'}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Listing Quality Scores */}
                                    {p.lastQualityCheckAt && (
                                      <div className="bg-white rounded-xl px-5 py-3.5 border border-slate-200 shadow-sm">
                                        <div className="flex items-center gap-2 mb-3">
                                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Listing Quality</p>
                                          <span className="text-[9px] text-slate-400">checked {timeAgo(p.lastQualityCheckAt)}</span>
                                        </div>
                                        <div className="flex flex-wrap gap-3">
                                          <div className="flex items-center gap-1.5">
                                            <span className="text-[10px] font-semibold text-slate-500">Bullets</span>
                                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${p.bulletPointCount >= 5 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                                              {p.bulletPointCount ?? '—'}/5
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-1.5">
                                            <span className="text-[10px] font-semibold text-slate-500">Images</span>
                                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${p.imageCount >= 5 ? 'bg-emerald-50 text-emerald-700' : p.imageCount >= 3 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-600'}`}>
                                              {p.imageCount ?? '—'}
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-1.5">
                                            <span className="text-[10px] font-semibold text-slate-500">Search Terms</span>
                                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${p.hasSearchTerms ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                                              {p.hasSearchTerms ? 'Yes' : 'No'}
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-1.5">
                                            <span className="text-[10px] font-semibold text-slate-500">A+</span>
                                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${p.hasAPlusContent ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                                              {p.hasAPlusContent ? 'Yes' : 'No'}
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-1.5">
                                            <span className="text-[10px] font-semibold text-slate-500">Title</span>
                                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${p.titleLength >= 80 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                              {p.titleLength ?? '—'} chars
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {/* Product Identifiers & Meta */}
                                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 bg-white rounded-xl px-5 py-3.5 border border-slate-200 shadow-sm">
                                      <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">ASIN</span>
                                        <code className="text-xs text-slate-700 font-mono bg-slate-100 px-2 py-0.5 rounded">{p.asin}</code>
                                        <button onClick={(e) => { e.stopPropagation(); copyToClipboard(p.asin); }} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" title="Copy ASIN">
                                          <Copy size={11} />
                                        </button>
                                      </div>
                                      {p.sku && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">SKU</span>
                                          <code className="text-xs text-slate-700 font-mono bg-slate-100 px-2 py-0.5 rounded">{p.sku}</code>
                                          <button onClick={(e) => { e.stopPropagation(); copyToClipboard(p.sku); }} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" title="Copy SKU">
                                            <Copy size={11} />
                                          </button>
                                        </div>
                                      )}
                                      {p.brand && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Brand</span>
                                          <span className="text-xs text-slate-700 font-medium">{p.brand}</span>
                                        </div>
                                      )}
                                      {p.openDate && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Listed</span>
                                          <span className="text-xs text-slate-600">{p.openDate}</span>
                                        </div>
                                      )}
                                      {p.lastSyncAt && (
                                        <div className="flex items-center gap-2 ml-auto">
                                          <span className="text-[10px] text-slate-400 flex items-center gap-1">
                                            <RefreshCw size={9} /> Synced {timeAgo(p.lastSyncAt)}
                                          </span>
                                        </div>
                                      )}
                                      {p.lastHijackCheckAt && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-[10px] text-slate-400 flex items-center gap-1">
                                            <ShoppingCart size={9} /> Hijack check {timeAgo(p.lastHijackCheckAt)}
                                          </span>
                                        </div>
                                      )}
                                      {p.asin && (
                                        <a href={`https://www.amazon.in/dp/${p.asin}`} target="_blank" rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#C45500] hover:text-[#C45500] transition-colors">
                                          <ExternalLink size={11} /> Amazon.in
                                        </a>
                                      )}
                                    </div>

                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50/50">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">Rows per page:</span>
                  <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
                    className="text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#f08804] focus:border-transparent">
                    {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">
                    {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, filteredProducts.length)} of {filteredProducts.length}
                  </span>
                  <button onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    <ChevronLeft size={13} /> Prev
                  </button>
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage >= totalPages - 1}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    Next <ChevronRight size={13} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
